import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsRestoring } from '@tanstack/react-query';
import {
  DialogForm,
  LtrToken,
  BidiText,
  EmptyState,
  EntityLoadState,
  SelectOptionsEmpty,
} from '../../../shared/components/common';
import { LocalizedCurrencyDisplay, LocalizedDateDisplay } from '../../../shared/components';
import { LocalizedMonetaryInput } from '../../../shared/components/LocalizedMonetaryInput';
import {
  CompanyCreateField,
  useCompanyCreatePrecedence,
} from '../../../shared/components/multi-company';
import { OrganizationPicker } from '../../master_data/contacts/components/OrganizationPicker';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { useAccounts } from '../hooks/useAccounts';
import { useInvoices } from '../hooks/useInvoices';
import { useBills } from '../hooks/useBills';
import { bankDepositApi } from '../services/friendlyApi';
import { filterCashAndBankAccounts } from '../utils/cashAndBankAccounts';
import {
  selectAllocatableInvoices,
  selectAllocatableBills,
  autoAllocateAmounts,
  autoAllocateBillAmounts,
  sumAllocations,
} from '../utils/paymentAllocation';
import type { PaymentCreate, PaymentType } from '../types';
import { toDateOnlyString } from '@/shared/utils/dateFormat';

// literal-enum-options-ok: accounting_payments.method_kind (a CUSTOMER payment
// method — cash, check, digital_wallet, other), not platform billing's
// billing_payment.method, which is how WE collect for the platform.
const PAYMENT_METHOD_KINDS = [
  'cash',
  'check',
  'ach',
  'wire',
  'card',
  'digital_wallet',
  'other',
] as const;

type PaymentMethodKind = (typeof PAYMENT_METHOD_KINDS)[number];

/** A stored `payment_method` is free text; only carry it over when it is one of
 * the kinds this dialog's picker actually offers. */
function isPaymentMethodKind(value: string | undefined): value is PaymentMethodKind {
  return !!value && (PAYMENT_METHOD_KINDS as readonly string[]).includes(value);
}

/** A document (invoice or bill) a payment can be allocated to — the shared shape
 * the allocation grid renders, projected from either an {@link Invoice} (AR) or a
 * {@link Bill} (AP) so one grid serves both payment directions. */
interface AllocatableDoc {
  id: string;
  number: string;
  amount_due: number;
  currency_code: string;
  due_date?: string | null;
}

interface PaymentQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: PaymentCreate) => void;
  isLoading?: boolean;
  defaultPaymentType?: PaymentType;
  /** Prefill the customer/vendor (e.g. opened from an invoice/bill's Record payment). */
  defaultContactId?: string;
  defaultContactName?: string;
  /**
   * The source document's company (ACC-J3/J5). A payment recorded against an
   * invoice/bill belongs to that document's legal entity, NOT to whatever the
   * app-bar scope happens to be — and with no scope selected there is nothing to
   * inherit at all. When set, the company field is locked and the
   * chart-of-accounts read (bank-account picker) is scoped to it.
   */
  defaultCompanyId?: string;
  /** Prefill an allocation to this invoice at its open balance (received only). */
  defaultInvoiceId?: string;
  /** Prefill an allocation to this bill at its open balance (made only). */
  defaultBillId?: string;
  /** Lock the contact picker when the payer is fixed by the source document. */
  lockContact?: boolean;
  /**
   * Prefill the bank account and method from the document's last recorded
   * payment. Two instalments against the same invoice almost always land the
   * same way, and re-picking both every time was pure re-keying. Ignored when
   * the caller has nothing to carry over. Both stay fully editable.
   */
  defaultDepositToAccountId?: string;
  defaultPaymentMethod?: string;
  /**
   * Seed the payment reference with the source document's own number (P2P-1's
   * `bill -> payment` carry). It is the number on the screen behind the dialog,
   * and the clerk was retyping it into an empty box on every bill. Fully
   * editable — a cheque or wire reference simply replaces it.
   */
  defaultReference?: string;
}

export function PaymentQuickCreateDialog({
  open,
  onClose,
  onSubmit,
  isLoading,
  defaultPaymentType,
  defaultContactId,
  defaultContactName,
  defaultCompanyId,
  defaultInvoiceId,
  defaultBillId,
  lockContact,
  defaultDepositToAccountId,
  defaultPaymentMethod,
  defaultReference,
}: PaymentQuickCreateDialogProps) {
  const { t } = useTranslation('accounting');
  // True while the persisted offline cache is being restored — React Query
  // fetches nothing in that window, so every query reads as "not loading, no
  // data" until it ends.
  const isRestoring = useIsRestoring();

  // Create-time company: the ONE shared precedence seam — the source
  // document's company (context) > global scope > sole company
  // (docs/platform/multi-company-design.md §3).
  const companyPrecedence = useCompanyCreatePrecedence(defaultCompanyId);
  const { companies, multiCompany, defaultCompanyId: resolvedCompanyId } = companyPrecedence;
  const [companyId, setCompanyId] = useState(defaultCompanyId ?? '');
  // Scope the chart to the resolved company so the bank-account picker offers
  // that entity's cash/bank accounts only. Unscoped, the read aggregates every
  // accessible company's chart — another company's accounts, and duplicate
  // rows wherever two charts share an account number (ACC-J3/J5).
  const { data: accountsData, isSuccess: accountsLoaded, isError: accountsFailed } = useAccounts({
    is_active: true,
    limit: 500,
    company_id: companyId || undefined,
  });
  const [paymentType, setPaymentType] = useState<PaymentType>(defaultPaymentType || 'received');
  const [contactId, setContactId] = useState('');
  const [contactName, setContactName] = useState('');
  const [amount, setAmount] = useState('');
  const [depositToAccountId, setDepositToAccountId] = useState('');
  const [paymentDate, setPaymentDate] = useState(toDateOnlyString(new Date()));
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodKind | ''>('');
  const [reference, setReference] = useState('');
  const [bankTransactionNumber, setBankTransactionNumber] = useState('');
  const [notes, setNotes] = useState('');
  // Per-document allocation amounts (keyed by invoice/bill id).
  const [allocations, setAllocations] = useState<Record<string, number | null>>({});

  useEffect(() => {
    if (open) {
      // The source document's company wins over the app-bar scope — the payment
      // posts to the document's legal entity either way (ACC-J3/J5).
      // Seed from the SYNCHRONOUS context prop only — the scope/sole-company
      // fallback lands via the fill effect below (see its comment).
      setCompanyId(defaultCompanyId ?? '');
      setPaymentType(defaultPaymentType || 'received');
      setContactId(defaultContactId ?? '');
      setContactName(defaultContactName ?? '');
      setAmount('');
      // Carry the previous payment's routing over (see defaultDepositToAccountId).
      setDepositToAccountId(defaultDepositToAccountId ?? '');
      setPaymentDate(toDateOnlyString(new Date()));
      setPaymentMethod(
        isPaymentMethodKind(defaultPaymentMethod) ? defaultPaymentMethod : '',
      );
      setReference(defaultReference ?? '');
      setBankTransactionNumber('');
      setNotes('');
      setAllocations({});
    }
  }, [
    open,
    defaultCompanyId,
    defaultPaymentType,
    defaultContactId,
    defaultContactName,
    defaultDepositToAccountId,
    defaultPaymentMethod,
    defaultReference,
  ]);

  // The scope/sole-company fallback needs the accessible-company list, so
  // `resolvedCompanyId` settles AFTER the first render. It must NOT sit in the
  // reset effect above — that effect rebuilds the WHOLE form, so a late
  // resolution would wipe everything typed since the dialog opened. Fill the
  // still-blank company field on its own instead; a user pick (or the source
  // document's company, seeded synchronously above) is never overwritten.
  useEffect(() => {
    if (open && !companyId && resolvedCompanyId) setCompanyId(resolvedCompanyId);
  }, [open, companyId, resolvedCompanyId]);

  const isMade = paymentType === 'made';

  const accounts = accountsData?.data;
  const bankAccounts = useMemo(
    () => filterCashAndBankAccounts(accounts || []),
    [accounts],
  );

  // With no company resolved the chart read is unscoped, so the picker lists
  // every accessible company's cash/bank accounts — and two charts routinely
  // number their operating bank the same, which renders as two identical rows
  // ("1010 - Business Checking Account" twice). Name the owning entity on each
  // option so the choice is decidable; once a company is picked the list is
  // single-entity and the suffix would be noise (ACC-J3/J5).
  const crossCompanyChart = multiCompany && !companyId;
  const companyNameById = useMemo(
    () => new Map(companies.map((c) => [c.id, c.name])),
    [companies],
  );

  // Switching company invalidates a deposit account picked from the previous
  // company's chart — drop it rather than posting a payment to an account the
  // picker no longer offers.
  useEffect(() => {
    if (!accountsLoaded) return;
    if (!depositToAccountId) return;
    if (bankAccounts.some((acc) => acc.id === depositToAccountId)) return;
    setDepositToAccountId('');
  }, [accountsLoaded, bankAccounts, depositToAccountId]);

  // T19 — when payment method kind changes on a received payment, ask the
  // backend for the default deposit-to account (clearing for cash/check,
  // configured bank for ach/wire/card/digital_wallet) and pre-select it
  // unless the user has already picked one. Deposit routing is configured per
  // tenant, so honor the suggestion only when it is in this company's chart.
  useEffect(() => {
    if (!open) return;
    if (paymentType !== 'received') return;
    if (!paymentMethod) return;
    if (depositToAccountId) return; // honor explicit user choice
    let cancelled = false;
    bankDepositApi
      .defaultRouting(paymentMethod)
      .then((res) => {
        if (cancelled) return;
        const suggested = res.data?.deposit_to_account_id;
        if (suggested && bankAccounts.some((acc) => acc.id === suggested)) {
          setDepositToAccountId(suggested);
        }
      })
      .catch(() => {
        /* non-fatal; user can still pick manually */
      });
    return () => {
      cancelled = true;
    };
  }, [open, paymentType, paymentMethod, depositToAccountId, bankAccounts]);

  // Open receivables (AR) or payables (AP) for the selected contact. Gated on
  // `open` + the matching direction so a mounted-but-closed dialog fetches
  // nothing, and a received payment never fetches bills (and vice-versa).
  const showAllocations = !!contactId;
  const {
    data: invoicesData,
    isLoading: invoicesLoading,
    isError: invoicesFailed,
    error: invoicesError,
    refetch: refetchInvoices,
  } = useInvoices(
    !isMade && contactId
      ? { customer_id: contactId, company_id: companyId || undefined, limit: 200 }
      : undefined,
    { enabled: open && showAllocations && !isMade },
  );
  // Fetch the vendor's bills and let `selectAllocatableBills` pick the open ones
  // client-side. NB: don't pass `status: 'due'` — the list endpoint narrows that
  // to `due_date >= today`, which would drop OVERDUE (still-open) bills; the
  // stored lifecycle status of an overdue bill is 'due', so the client filter
  // (status==='due' && amount_due>0) catches them while paid bills (amount_due=0)
  // fall out.
  const {
    data: billsData,
    isLoading: billsLoading,
    isError: billsFailed,
    error: billsError,
    refetch: refetchBills,
  } = useBills(
    isMade && contactId
      ? { vendor_id: contactId, company_id: companyId || undefined, limit: 200 }
      : undefined,
    { enabled: open && showAllocations && isMade },
  );

  // A payment settles one currency; anchor to the source document (document-driven
  // flow) or the first open document and show only same-currency open items.
  // A payment also settles ONE legal entity, so the open documents offered are
  // the resolved company's — the contact-scoped list endpoints are not company
  // filtered, and a customer can trade with several of the tenant's companies.
  const { openInvoices, allocationCurrency: invoiceCurrency } = useMemo(
    () =>
      selectAllocatableInvoices(
        (invoicesData?.data ?? []).filter((inv) => !companyId || inv.company_id === companyId),
        defaultInvoiceId,
      ),
    [invoicesData, defaultInvoiceId, companyId],
  );
  const { openBills, allocationCurrency: billCurrency } = useMemo(
    () =>
      selectAllocatableBills(
        (billsData?.data ?? []).filter((bill) => !companyId || bill.company_id === companyId),
        defaultBillId,
      ),
    [billsData, defaultBillId, companyId],
  );
  const allocationCurrency = isMade ? billCurrency : invoiceCurrency;
  const allocationsLoading = isMade ? billsLoading : invoicesLoading;
  // The active direction's raw query result — the load-state branch must read
  // the RAW response, not `openDocuments.length`, which is 0 on a failed load too.
  const allocationsData = isMade ? billsData : invoicesData;
  const allocationsFailed = isMade ? billsFailed : invoicesFailed;
  const allocationsError = isMade ? billsError : invoicesError;
  const refetchAllocations = isMade ? refetchBills : refetchInvoices;
  const showAllocationsLoadState =
    allocationsData === undefined && (allocationsFailed || isRestoring);

  // Unified rows for the allocation grid — one shape, both directions.
  const openDocuments = useMemo<AllocatableDoc[]>(
    () =>
      isMade
        ? openBills.map((bill) => ({
            id: bill.id,
            number: bill.bill_number,
            amount_due: bill.amount_due,
            currency_code: bill.currency_code,
            due_date: bill.due_date ?? null,
          }))
        : openInvoices.map((inv) => ({
            id: inv.id,
            number: inv.invoice_number,
            amount_due: inv.amount_due,
            currency_code: inv.currency_code,
            due_date: inv.due_date ?? null,
          })),
    [isMade, openBills, openInvoices],
  );

  // Seed the source document's full open balance once its data has loaded.
  const sourceDocId = isMade ? defaultBillId : defaultInvoiceId;
  useEffect(() => {
    if (!open || !sourceDocId) return;
    const source = openDocuments.find((doc) => doc.id === sourceDocId);
    if (!source) return;
    setAllocations((prev) =>
      prev[sourceDocId] === undefined
        ? { ...prev, [sourceDocId]: Number(source.amount_due) }
        : prev,
    );
  }, [open, sourceDocId, openDocuments]);

  const allocationTotal = useMemo(() => sumAllocations(allocations), [allocations]);
  const isAllocating = showAllocations && allocationTotal > 0;
  const effectiveAmount = isAllocating ? allocationTotal : parseFloat(amount);
  const anyLineOverDue = openDocuments.some(
    (doc) => Number(allocations[doc.id] || 0) > Number(doc.amount_due) + 0.0001,
  );

  const setLineAmount = (docId: string, value: number | null) =>
    setAllocations((prev) => ({ ...prev, [docId]: value }));

  /** Greedily fill each open document's balance up to the entered amount, or fill
   * every balance when no amount is set yet. */
  const autoAllocate = () =>
    setAllocations(
      isMade
        ? autoAllocateBillAmounts(openBills, parseFloat(amount))
        : autoAllocateAmounts(openInvoices, parseFloat(amount)),
    );

  // A truthy prefill is not proof that the account belongs to the resolved
  // company's active chart. Require the successful account read to verify
  // membership before enabling either the button or the submit handler.
  const depositAccountIsAvailable =
    accountsLoaded && bankAccounts.some((acc) => acc.id === depositToAccountId);
  const canSubmit =
    depositAccountIsAvailable &&
    effectiveAmount > 0 &&
    !anyLineOverDue &&
    !(multiCompany && !companyId);

  const handleSubmit = () => {
    if (!canSubmit) return;
    // Rule 2's acknowledgement — here rather than in `canSubmit`, which drives
    // `isDisabled`: a greyed-out button explains nothing.
    if (companyPrecedence.blockCreate(companyId)) return;

    const lines = isAllocating
      ? openDocuments
          .filter((doc) => Number(allocations[doc.id] || 0) > 0)
          .map((doc) => ({
            allocated_to_type: isMade ? 'bill' : 'invoice',
            allocated_to_id: doc.id,
            amount: Number(allocations[doc.id]),
          }))
      : undefined;

    onSubmit({
      company_id: companyId || undefined,
      payment_type: paymentType,
      amount: effectiveAmount,
      deposit_to_account_id: depositToAccountId,
      payment_date: paymentDate,
      contact_id: contactId || undefined,
      contact_name: contactName || undefined,
      currency_code: isAllocating ? allocationCurrency : undefined,
      payment_method: paymentMethod || undefined,
      payment_method_kind: paymentMethod || undefined,
      reference: reference || undefined,
      bank_transaction_number: bankTransactionNumber || undefined,
      notes: notes || undefined,
      allocations: lines,
    });
  };

  return (
    // One action, one name. Every entry point into this dialog is labelled
    // "Record payment" (an invoice's and a bill's detail page and list row), so
    // the dialog and its submit button say the same thing rather than switching
    // to "Create Payment" mid-flow. The record the action produces — a Receipt
    // on the customer side, a Payment on the vendor side — is named by the
    // success toast, via `paymentCreatedMessageKey`.
    <DialogForm
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={t('payments.recordPayment')}
      submitLabel={t('payments.recordPayment')}
      isLoading={isLoading}
      isDisabled={!canSubmit}
      maxWidth={showAllocations ? 'lg' : 'md'}
    >
      <div className="space-y-4">
        {/* Document-driven: the company is fixed by the invoice/bill being paid,
            so the field shows it read-only rather than inviting a cross-company
            payment. */}
        <CompanyCreateField
          precedence={companyPrecedence}
          value={companyId}
          onChange={setCompanyId}
          id="payment-company"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('payments.type')}</Label>
            <Select value={paymentType} onValueChange={(v) => setPaymentType(v as PaymentType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="received">{t('paymentTypes.received')}</SelectItem>
                <SelectItem value="made">{t('paymentTypes.made')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t('payments.date')}</Label>
            <Input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>
            {paymentType === 'received' ? t('payments.customer') : t('payments.vendor')}
          </Label>
          {lockContact ? (
            <Input value={contactName} disabled />
          ) : (
            <OrganizationPicker
              value={contactId}
              onChange={(id) => setContactId(id)}
              onCompanySelected={(company) => setContactName(company?.name ?? '')}
              organizationRelationshipFilter={paymentType === 'received' ? 'customer' : 'vendor'}
              placeholder={t('payments.contactPlaceholder')}
              allowClear
            />
          )}
        </div>

        {/* Allocation grid — apply the payment to the contact's open invoices (AR)
            or open bills (AP). */}
        {showAllocations && (
          <div className="space-y-2 rounded-md border border-border p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {isMade ? t('payments.applyToBills') : t('payments.applyToInvoices')}
              </span>
              {openDocuments.length > 0 && (
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={autoAllocate}
                >
                  {t('payments.autoAllocate')}
                </button>
              )}
            </div>
            {showAllocationsLoadState ? (
              <EntityLoadState
                entityType={isMade ? 'bills' : 'invoices'}
                variant="list"
                data={allocationsData}
                error={allocationsError}
                isLoading={allocationsLoading}
                onRetry={refetchAllocations}
              />
            ) : allocationsLoading ? (
              <p className="text-sm text-muted-foreground">{t('common:app.loading', 'Loading…')}</p>
            ) : openDocuments.length === 0 ? (
              <EmptyState
                title={isMade ? t('payments.noOpenBillsTitle') : t('payments.noOpenInvoicesTitle')}
                message={isMade ? t('payments.noOpenBillsMessage') : t('payments.noOpenInvoicesMessage')}
              />
            ) : (
              <div className="space-y-2">
                {openDocuments.map((doc) => {
                  const lineOver =
                    Number(allocations[doc.id] || 0) > Number(doc.amount_due) + 0.0001;
                  return (
                    <div
                      key={doc.id}
                      className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">
                          <LtrToken>{doc.number}</LtrToken>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('payments.due')}:{' '}
                          <LocalizedCurrencyDisplay
                            value={doc.amount_due}
                            currency={doc.currency_code}
                          />
                          {doc.due_date && (
                            <>
                              {' · '}
                              <LocalizedDateDisplay dateOnly date={doc.due_date} />
                            </>
                          )}
                        </p>
                      </div>
                      <div className="w-full sm:w-44">
                        <LocalizedMonetaryInput
                          value={allocations[doc.id] ?? null}
                          onChange={(v) => setLineAmount(doc.id, v)}
                          currency={doc.currency_code}
                          min={0}
                          max={Number(doc.amount_due)}
                          aria-label={t('payments.amount')}
                        />
                        {lineOver && (
                          <p className="mt-0.5 text-xs text-destructive">
                            {t('payments.exceedsDue')}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('payments.amount')}</Label>
            {isAllocating && allocationCurrency ? (
              <div
                className="flex h-10 items-center rounded-md border border-border bg-muted/40 px-3 text-sm font-semibold"
                data-testid="payment-amount"
              >
                <LocalizedCurrencyDisplay value={allocationTotal} currency={allocationCurrency} />
              </div>
            ) : (
              <LocalizedMonetaryInput
                min={0}
                value={amount === '' ? null : Number(amount)}
                onChange={(v) => setAmount(v == null ? '' : String(v))}
                data-testid="payment-amount"
                aria-label={t('payments.amount')}
              />
            )}
          </div>
          <div className="space-y-2">
            <Label required>{t('payments.bankAccount')}</Label>
            <Select value={depositToAccountId} onValueChange={setDepositToAccountId}>
              <SelectTrigger data-testid="payment-bank-account" aria-label={t('payments.bankAccount')}>
                <SelectValue placeholder={t('payments.selectBankAccount')} />
              </SelectTrigger>
              <SelectContent>
                {bankAccounts.length === 0 && (
                  // The chart this company posts against carries no cash/bank
                  // account — say so instead of opening a blank popup (WI 187df059).
                  <SelectOptionsEmpty
                    emptyMessage={t('accounts.noCashOrBankAccounts')}
                    loaded={accountsLoaded}
                    failed={accountsFailed}
                  />
                )}
                {bankAccounts.map((acc) => {
                  const ownerName = crossCompanyChart
                    ? companyNameById.get(acc.company_id)
                    : undefined;
                  return (
                    <SelectItem key={acc.id} value={acc.id}>
                      <LtrToken>{acc.account_number}</LtrToken> - <BidiText>{acc.name}</BidiText>
                      {ownerName && (
                        <span className="text-muted-foreground">
                          {' · '}
                          <BidiText>{ownerName}</BidiText>
                        </span>
                      )}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t('payments.method')}</Label>
          <Select value={paymentMethod} onValueChange={(v) => setPaymentMethod(v as PaymentMethodKind)}>
            <SelectTrigger data-testid="payment-method" aria-label={t('payments.method')}>
              <SelectValue placeholder={t('payments.methodPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {PAYMENT_METHOD_KINDS.map((kind) => (
                <SelectItem key={kind} value={kind}>
                  {t(`payments.methodKinds.${kind}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {paymentType === 'received' && (
          <p className="text-xs text-muted-foreground">
            {t('payments.routingHint')}
          </p>
        )}

        <div className="space-y-2">
          <Label>{t('payments.reference')}</Label>
          <Input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder={t('payments.referencePlaceholder')}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('payments.bankTransactionNumber')}</Label>
          <Input
            value={bankTransactionNumber}
            onChange={(e) => setBankTransactionNumber(e.target.value)}
            placeholder={t('payments.bankTransactionNumberPlaceholder')}
            maxLength={50}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('payments.notes')}</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />
        </div>
      </div>
    </DialogForm>
  );
}
